// BUILD-042 (Oct 17) - the actionable lists. Two small presentational pieces over the server-backed slices in attentionStore.ts:
//   <MyAttentionList>      - the employee's own items (My HR overview). Every row links to the exact document / checklist.
//   <AdminAttentionBanner> - HR's items across employees (HR Administration). Yellow only while something is waiting; absent at zero.
// Neither can be dismissed: they change only when the underlying work changes. A failed check shows a plain "could not check" message with
// a retry - never an empty list that reads as "all clear".
import { useNavigate } from 'react-router-dom';
import { refreshAttention } from '../attentionStore';
import {
  adminItemPath, myItemPath, pendingBreakdownText, KIND_PHRASE, adminActionPhrase, type AdminAttention, type AttentionSlice, type MyAttention,
} from './attentionModel';
import { formatLocalDate } from '../hrFormat';

/** The specific status and the action that opens the existing signing / upload flow (the document row opens ready to act). */
const MY_ITEM_CHIP: Record<MyAttention['items'][number]['kind'], string> = {
  signature: 'Signature needed', document: 'Upload needed', correction: 'Correction needed', task: 'Task to do',
};
const MY_ITEM_ACTION: Record<MyAttention['items'][number]['kind'], string> = {
  signature: 'Review & sign', document: 'Upload', correction: 'Correct & resubmit', task: 'Open task',
};

function CouldNotCheck({ what }: { what: string }) {
  return (
    <div role="alert" className="flex items-center justify-between gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="attention-error">
      <span>We could not check {what} just now. This is not the same as nothing being there.</span>
      <button type="button" onClick={refreshAttention} className="text-xs underline flex-shrink-0">Try again</button>
    </div>
  );
}

export function MyAttentionList({ slice, title = 'Needs your action' }: { slice: AttentionSlice<MyAttention>; title?: string }) {
  const navigate = useNavigate();
  const data = slice.data;
  return (
    <section aria-label={title} data-testid="my-attention-list">
      {slice.status === 'error' ? (
        <CouldNotCheck what="what needs your action" />
      ) : !data ? (
        <div className="text-sm text-gray-400">Checking…</div>
      ) : data.items.length === 0 ? (
        <div className="text-sm text-gray-500" data-testid="my-attention-empty">You are all caught up. Anything waiting on HR is shown in Documents and Checklists, and needs nothing from you.</div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {data.items.map((i) => (
            <li key={i.itemId} className="flex items-start justify-between gap-3 py-2.5 first:pt-0" data-testid="my-attention-item" data-kind={i.kind}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900 truncate">{i.title}</span>
                  {/* Oct 18: ONE specific yellow status, never a generic "Pending". */}
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-md text-amber-900 bg-amber-100 border border-amber-300" data-testid="my-attention-chip">{MY_ITEM_CHIP[i.kind]}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{i.detail}{i.dueDate ? ` · due ${formatLocalDate(i.dueDate)}` : ''}</div>
              </div>
              <button type="button" onClick={() => navigate(myItemPath(i))} className="text-xs font-semibold text-[#176A9B] border border-[#4AABDE] rounded-md px-2.5 py-1 hover:bg-[#EDF5FC] flex-shrink-0" aria-label={`${KIND_PHRASE[i.kind]}: ${i.title}`} data-testid="my-attention-action">
                {MY_ITEM_ACTION[i.kind]} →
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** HR's attention banner. `limit` caps the rows shown here only - the number in the heading is always the exact total. */
export function AdminAttentionBanner({ slice, limit = 5, pendingPath = '/hr/admin/pending', reviewerView = false }: { slice: AttentionSlice<AdminAttention>; limit?: number; pendingPath?: string; reviewerView?: boolean }) {
  const navigate = useNavigate();
  if (slice.status === 'error') return <div className="mb-4"><CouldNotCheck what="what is waiting for HR" /></div>;
  const data = slice.data;
  if (!data || data.authorized === false || data.total <= 0) return null; // zero (or unknown / not authorized): no warning is drawn
  // A reviewer's view (not an HR administrator) is about correction requests only, whatever else the server happens to count for them.
  const scopedItems = reviewerView ? data.items.filter((i) => i.kind === 'correction') : data.items;
  const scopedTotal = reviewerView ? (data.counts?.corrections ?? scopedItems.length) : data.total;
  if (scopedTotal <= 0) return null;
  const shown = scopedItems.slice(0, limit);
  return (
    <section className="mb-4 bg-amber-50 rounded-lg border border-amber-300 shadow-sm p-4" aria-label="Open HR work" data-testid="admin-attention-banner">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900 bg-amber-300 rounded px-1.5 py-0.5">Attention</span>
        <h2 className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
          Open work <span className="text-amber-700" data-testid="admin-attention-total">({scopedTotal})</span>
        </h2>
        <button type="button" onClick={() => navigate(pendingPath)} className="ml-auto text-xs text-[#176A9B] hover:underline" data-testid="admin-attention-view-all">View all pending work →</button>
      </div>
      <p className="text-xs text-amber-900 mb-2" data-testid="admin-attention-breakdown">{reviewerView
          ? `${scopedTotal} correction ${scopedTotal === 1 ? 'request' : 'requests'} waiting for your review`
          : pendingBreakdownText(data.counts ?? { awaitingEmployee: 0, needsHrReview: 0, tasks: 0 })}</p>
      <ul className="divide-y divide-amber-200">
        {shown.map((i) => (
          <li key={i.itemId} className="flex items-start justify-between gap-3 py-2 first:pt-0">
            <div className="min-w-0">
              <div className="text-sm text-gray-900 truncate"><span className="font-semibold">{i.employeeName ?? 'Employee'}</span> · {i.title}</div>
              <div className="text-xs text-gray-600 mt-0.5">{i.detail}</div>
            </div>
            <button type="button" onClick={() => navigate(adminItemPath(i))} className="text-xs text-[#176A9B] hover:underline flex-shrink-0 mt-0.5" aria-label={`${adminActionPhrase(i.kind)}: ${i.title} for ${i.employeeName ?? 'this employee'}`} data-testid="admin-attention-action">
              {adminActionPhrase(i.kind)} →
            </button>
          </li>
        ))}
      </ul>
      {scopedTotal > shown.length && (
        <p className="text-xs text-amber-900 mt-2" data-testid="admin-attention-more">
          Showing {shown.length} of {scopedTotal}. {reviewerView ? 'Open “View all pending work” for the rest.' : 'Each employee row in the list below shows how many of these are theirs.'}
        </p>
      )}
    </section>
  );
}
